import { AbstractHorizontalContainer } from "../model/containers";
import {
    Chord,
    NoteDuration,
    TimeSignature,
    Tone
} from "../model/core";
import { GridResolution } from "../model/grid";

import { assert } from "../utils/error";


export namespace CSS {
    export function VAR(name: string): string {
        return window.getComputedStyle(document.body).getPropertyValue(name);
    }
}

const NOTE_WIDTH = Number.parseInt(CSS.VAR('--note-width'));
const NOTE_HEIGHT = Number.parseInt(CSS.VAR('--note-box-height'));

interface RenderedWidthOptionsBase {
    includeKeyboard?: boolean,
};

interface RenderedWidthOptionsWithHNC extends RenderedWidthOptionsBase {
    notes: AbstractHorizontalContainer<any>,
}

interface RenderedWidthOptionsWithLength extends RenderedWidthOptionsBase {
    length: number,
}

/** FIXME:
 * the zoom factor used her was buggy and alwasys = 1.
 * Check what it should do under changed zoom conditions (from const zoomFactor = useZoomFactor();)
 */
function getRenderedWidthForHNC(opts: RenderedWidthOptionsWithHNC | RenderedWidthOptionsWithLength) : string {
    const includeKeybed = opts.includeKeyboard || false;
    const length = 'length' in opts ? opts.length : opts.notes.getLength();

    const width = `calc( var(--note-width) * ${length} + ${includeKeybed ? 'var(--piano-roll-header-width)' : ''})`;
    return width;
}

const TONE_CSS_MAPPING = {
    'C':  'c',
    'C#': 'csharp',
    'D':  'd',
    'D#': 'dsharp',
    'E':  'e',
    'F':  'f',
    'F#': 'fsharp',
    'G':  'g',
    'G#': 'gsharp',
    'A':  'a',
    'A#': 'asharp',
    'B':  'b'
};

function getChordSynesthesiaColor(chord: Chord): string {
    return getToneSynesthesiaColor(chord.root);
}

function getToneSynesthesiaColor(tone: Tone): string {
    // --nsc- == "Note Synesthesia Color" ;-)
    // --nsc-scm- == Scriabin Color Mapping
    assert(tone !== undefined, 'tone is undefined');
    assert(tone instanceof Tone, 'tone is not an instance of Tone');

    return `--nsc-scm-${TONE_CSS_MAPPING[tone.getName()]}`;
}

function getToneColorBaseClassName(tone: Tone): string {
    assert(tone !== undefined, 'tone is undefined');
    assert(tone instanceof Tone, 'tone is not an instance of Tone');

    return `ryd-tone--${TONE_CSS_MAPPING[tone.getName()]}`;
}

function getNoteDurationSpriteCoordinates(duration: NoteDuration) : {x: number, y: number} {
    let x = 0;
    let y = 0;

    switch (duration.value) {
        case 2:
            x = 61;
            y = 86;
            break;
        case 4:
            x = 92;
            y = 86;
            break;
        case 8:
            x = 134;
            y = 86;
            break;
        case 16:
            x = 195;
            y = 86;
            break;
        case 32:
            x = 280;
            y = 86;
            break;
        case 64:
            x = 399;
            y = 86;
            break;

        default:
            console.log('duration', duration);
            throw new Error(`Invalid duration value ${duration}`);
    }
    return {x, y};
}

// FIXME: we tried originally with CSS.VAR('--piano-roll-header-width'), with can't be used as a number.
// So we use the fact that we just know that the header width is 32px.
const PIANO_ROLL_HEADER_WIDTH = 32;

function getLeftPositionForStart(
    start: number,
    resolution: NoteDuration,
    ppq: number,

): number {

    return (start * ppq * NOTE_WIDTH) + PIANO_ROLL_HEADER_WIDTH;
}

/**
 * Calculates the render box for a note given its start position and duration.
 * Zoom factor agnostic: gives proper values for zoomFactor = 1.
*/
function getRenderBoxForStartAndDuration(
    start: number,
    duration: NoteDuration,
    ppq: number
) : { left: number, width: number } {

    return {
        left: getLeftPositionForStart(start, duration, ppq),
        width: NOTE_WIDTH * duration.value / 4
    };
}

function estimateVisibleOffsetRange(
    leftOffset: number,
    resolution: NoteDuration,
    hncResolution: NoteDuration,
    width: number = 1600
) {
    const left = leftOffset;
    const right = left + width;
    const resolutionFactor = resolution.asCountedIn(hncResolution);

    return {
        min: left / NOTE_WIDTH / resolutionFactor - 20,
        max: right / NOTE_WIDTH / resolutionFactor
    };
}

export function normalizeClassName(s: string[]): string {
    return s.join(' ');
}

export function calculateNormalizedBarWidth(
    timeSignature?: TimeSignature,
    hnc?: AbstractHorizontalContainer<any>
) {
    return timeSignature && hnc ?
        timeSignature.numerator * NoteDuration.fromTimeSignature(timeSignature.denominator, hnc.getPPQ()).value // / hnc.resolution.value
        : 0;
}

export function getNewNoteDurationForInsert(
    newNoteDuration: NoteDuration,
    ppq: number,
    factor: number = 16
) : NoteDuration {
    // originally new NoteDuration(newNoteDuration.value * hnc.getPPQ() / 16)
    if (!newNoteDuration) {
        return new NoteDuration(1);
    }
    if (newNoteDuration.value === 1) {
        return newNoteDuration;
    }
    return new NoteDuration(newNoteDuration.value * ppq / factor);
}

export function convertToCSSWidth(
    duration: NoteDuration,
    zoomFactor: number
) {
    return `calc(((${duration.value} * var(--note-width) ) / ${zoomFactor}) - var(--note-duration-handle-width))`;
}

export function convertStartToCSSOffset(
    start: number,
    zoomFactor: number
) {
    return `calc((${start} * var(--note-width)) / ${zoomFactor} + var(--piano-roll-header-width))`;
}

export function coordinatesToTick(clientX: number, zoomFactor: number, noteWidth: number) {
    return 0 | (clientX * zoomFactor / noteWidth);
}

export function getLeftNearestGridPoint(
    tick: number,
    ppq: number,
    zoomFactor: number,
    grid: GridResolution,
    ts?: TimeSignature) {

    const gridSize = gridResolutionToNormalizedWidth(grid, ppq, ts);
    return (0| (tick / gridSize)) * gridSize;
}

export const gridResolutionToNormalizedWidth = (
    gridResolution: GridResolution,
    ppq: number,
    timeSignature?: TimeSignature,
): number => {
    const denominator = timeSignature?.denominator || 4;
    // reference values
    const beat = ppq * 4 / denominator;
    const bar = beat * (timeSignature?.numerator || 4);

    switch (gridResolution) {
        case 'bar': return bar;
        case 'beat': return beat;
        case '1/2 beat': return beat / 2;
        case '1/4 beat': return beat / 4;
        case '1/8 beat': return beat / 8;
        case '1/16 beat': return beat / 16;
        case '1/32 beat': return beat / 32;
        case '1/64 beat': return beat / 64;
        default: return bar;
    }
};

export function tickToPosition(noteWidth: number, zoomFactor: number, tick: number) {
    return tick * noteWidth / (zoomFactor);
}

export function positionToTick(noteWidth: number, zoomFactor: number, x: number) {
    return 0 | x * zoomFactor / noteWidth;
}

export {
    getLeftPositionForStart,
    getRenderedWidthForHNC,
    getChordSynesthesiaColor,
    getToneSynesthesiaColor,
    getNoteDurationSpriteCoordinates,
    getRenderBoxForStartAndDuration,
    NOTE_WIDTH,
    NOTE_HEIGHT,
    estimateVisibleOffsetRange,
    getToneColorBaseClassName
};
